<script lang="ts">
  import { onMount } from 'svelte';

  const SPLASH_DURATION_FULL = 2700;
  const SPLASH_DURATION_REDUCED = 800;

  function reducedMotion(): boolean {
    return (
      typeof matchMedia !== 'undefined' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  let fading = $state(false);
  let hidden = $state(false);
  let reduced = $state(false);
  let timer: ReturnType<typeof setTimeout> | null = null;

  function dismiss() {
    if (fading) return;
    fading = true;
    if (timer) clearTimeout(timer);
    setTimeout(
      () => {
        hidden = true;
      },
      reduced ? 200 : 650
    );
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      dismiss();
    }
  }

  onMount(() => {
    reduced = reducedMotion();
    const dur = reduced ? SPLASH_DURATION_REDUCED : SPLASH_DURATION_FULL;
    timer = setTimeout(dismiss, dur);
    return () => {
      if (timer) clearTimeout(timer);
    };
  });

  // Allow external replay
  export function play() {
    fading = false;
    hidden = false;
    reduced = reducedMotion();
    if (timer) clearTimeout(timer);
    timer = setTimeout(dismiss, reduced ? SPLASH_DURATION_REDUCED : SPLASH_DURATION_FULL);
  }
</script>

{#if !hidden}
  <div
    class="splash"
    class:fading
    role="button"
    tabindex="0"
    aria-label="Skip splash"
    onclick={dismiss}
    onkeydown={onKeyDown}
  >
    <div class="splash-rig" aria-hidden="true">
      <span class="splash-orbit splash-orbit--left"></span>
      <span class="splash-orbit splash-orbit--right"></span>
      <span class="splash-ring splash-ring--1"></span>
      <span class="splash-ring splash-ring--2"></span>
      <span class="splash-ring splash-ring--3"></span>
      <div class="splash-mark">V</div>
    </div>
    <div class="splash-wordmark">VuTwin</div>
    <div class="splash-tagline">FIND · YOUR · TWIN</div>
    <div class="splash-skip">tap to skip</div>
  </div>
{/if}

<style>
  /* Respect Reduce Motion: disable every keyframe in this component and
     show the final static composition immediately. The JS timer also
     shortcuts to 800ms in this mode. */
  @media (prefers-reduced-motion: reduce) {
    .splash,
    .splash *,
    .splash::before,
    .splash::after {
      animation: none !important;
      transition: none !important;
      transform: none !important;
      opacity: 1 !important;
    }
  }

  .splash {
    position: fixed;
    inset: 0;
    background: var(--bg);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    pointer-events: auto;
    cursor: pointer;
  }
  :global(body:not(.brutal)) .splash::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 30% 35%, rgba(255, 216, 74, 0.18), transparent 55%),
      radial-gradient(circle at 70% 65%, rgba(255, 56, 188, 0.20), transparent 55%);
    pointer-events: none;
  }
  .splash.fading {
    animation: splashFadeOut 600ms cubic-bezier(0.4, 0, 0.6, 1) forwards;
  }
  @keyframes splashFadeOut {
    to {
      opacity: 0;
      visibility: hidden;
    }
  }

  .splash-rig {
    position: relative;
    width: 240px;
    height: 240px;
  }

  .splash-orbit {
    position: absolute;
    top: 50%;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    transform: translateY(-50%);
    opacity: 0;
    z-index: 2;
  }
  .splash-orbit--left {
    background: var(--gold);
    box-shadow:
      0 0 30px var(--gold),
      0 0 80px rgba(255, 216, 74, 0.6);
    animation: splashOrbitLeft 1100ms cubic-bezier(0.7, 0, 0.3, 1) 100ms forwards;
  }
  .splash-orbit--right {
    background: var(--hot);
    box-shadow:
      0 0 30px var(--hot),
      0 0 80px rgba(255, 56, 188, 0.6);
    animation: splashOrbitRight 1100ms cubic-bezier(0.7, 0, 0.3, 1) 100ms forwards;
  }
  :global(body.brutal) .splash-orbit {
    border-radius: 0;
    background: var(--text);
    box-shadow: none;
    width: 24px;
    height: 24px;
  }
  @keyframes splashOrbitLeft {
    0% {
      left: -10vw;
      opacity: 0;
    }
    20% {
      opacity: 1;
    }
    85% {
      left: 50%;
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    100% {
      left: 50%;
      opacity: 0;
      transform: translate(-50%, -50%) scale(0);
    }
  }
  @keyframes splashOrbitRight {
    0% {
      right: -10vw;
      opacity: 0;
    }
    20% {
      opacity: 1;
    }
    85% {
      right: 50%;
      opacity: 1;
      transform: translate(50%, -50%) scale(1);
    }
    100% {
      right: 50%;
      opacity: 0;
      transform: translate(50%, -50%) scale(0);
    }
  }

  .splash-mark {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 140px;
    height: 140px;
    border-radius: 32px;
    background: linear-gradient(135deg, var(--gold) 0%, var(--hot) 50%, var(--violet) 100%);
    display: grid;
    place-items: center;
    color: var(--text-on-light);
    font-weight: 900;
    font-size: 72px;
    box-shadow:
      0 0 60px rgba(255, 216, 74, 0.5),
      0 0 120px rgba(255, 56, 188, 0.4),
      0 12px 0 0 var(--hot-deep);
    transform: scale(0);
    z-index: 3;
    animation: splashMarkIn 800ms cubic-bezier(0.34, 1.56, 0.64, 1) 1000ms forwards;
  }
  :global(body.brutal) .splash-mark {
    background: var(--text);
    color: var(--bg);
    border: 4px solid var(--text);
    box-shadow: 10px 10px 0 0 var(--text);
    border-radius: 0;
  }
  @keyframes splashMarkIn {
    0% {
      transform: scale(0) rotate(-180deg);
    }
    60% {
      transform: scale(1.2) rotate(15deg);
    }
    100% {
      transform: scale(1) rotate(0deg);
    }
  }

  .splash-ring {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 140px;
    height: 140px;
    border-radius: 32px;
    border: 2px solid var(--hot);
    opacity: 0;
    z-index: 1;
    pointer-events: none;
  }
  :global(body.brutal) .splash-ring {
    border-radius: 0;
    border-color: var(--text);
    border-width: 3px;
  }
  .splash-ring--1 {
    animation: splashRing 1.6s ease-out 1500ms infinite;
  }
  .splash-ring--2 {
    animation: splashRing 1.6s ease-out 1900ms infinite;
  }
  .splash-ring--3 {
    animation: splashRing 1.6s ease-out 2300ms infinite;
  }
  @keyframes splashRing {
    0% {
      transform: scale(1);
      opacity: 0.7;
    }
    100% {
      transform: scale(3.6);
      opacity: 0;
    }
  }

  .splash-wordmark {
    margin-top: 40px;
    font-size: 42px;
    font-weight: 900;
    letter-spacing: -0.04em;
    line-height: 1;
    background: linear-gradient(135deg, var(--gold) 0%, var(--hot) 50%, var(--cyan) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    opacity: 0;
    animation: splashFadeUp 600ms cubic-bezier(0.2, 0.8, 0.2, 1) 1700ms forwards;
    -webkit-font-smoothing: antialiased;
    text-rendering: geometricPrecision;
    paint-order: stroke fill;
  }
  :global(body.brutal) .splash-wordmark {
    background: none;
    -webkit-text-fill-color: var(--text);
    color: var(--text);
    text-transform: uppercase;
  }
  .splash-tagline {
    margin-top: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.25em;
    color: var(--text-3);
    opacity: 0;
    animation: splashFadeUp 600ms cubic-bezier(0.2, 0.8, 0.2, 1) 2000ms forwards;
  }
  .splash-skip {
    position: absolute;
    bottom: max(var(--safe-cta-bottom), 24px);
    font-size: 10px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--text-3);
    font-weight: 700;
    opacity: 0;
    animation: splashFadeUp 400ms ease 2400ms forwards;
  }
  @keyframes splashFadeUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
